import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatCurrency } from '@/lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { useI18n } from '../contexts/I18nContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ShoppingCart, 
  Clock, 
  Users, 
  Package,
  Plus,
  ArrowRight,
  TrendingUp,
  TrendingDown,
  Euro,
  Star,
  BarChart3
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, isBuyer, isSeller, hasFullBuyerFeatures, isAssociatedOnly } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Solo per utenti con full buyer features o seller
    if (hasFullBuyerFeatures || isSeller) {
      fetchStats();
    } else {
      setLoading(false);
    }
  }, [hasFullBuyerFeatures, isSeller]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/stats`);
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: 'secondary',
      submitted: 'default',
      confirmed: 'default',
      in_preparation: 'default',
      ready: 'default',
      completed: 'default',
      cancelled: 'destructive'
    };
    const colors = {
      draft: 'bg-muted text-muted-foreground',
      submitted: 'bg-amber-500/10 text-amber-600 dark:text-amber-400',
      confirmed: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      in_preparation: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
      ready: 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400',
      completed: 'bg-green-500/10 text-green-600 dark:text-green-400',
      cancelled: 'bg-red-500/10 text-red-600 dark:text-red-400'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[status] || colors.draft}`}>
        {t(`orders.${status}`)}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-in" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-heading font-bold">{t('dashboard.welcome')}, {user?.name?.split(' ')[0]}</h1>
        <p className="text-muted-foreground mt-1">{t('dashboard.overview')}</p>
      </div>

      {/* Stats Grid - solo per utenti con full buyer features */}
      {hasFullBuyerFeatures && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="stagger-1" data-testid="stat-total-orders">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t('dashboard.totalOrders')}
              </CardTitle>
              <ShoppingCart className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{stats?.total_orders || 0}</div>
            </CardContent>
          </Card>

          <Card className="stagger-2" data-testid="stat-pending-orders">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t('dashboard.pendingOrders')}
              </CardTitle>
              <Clock className="h-4 w-4 text-amber-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-amber-600 dark:text-amber-400">
                {stats?.pending_orders || 0}
              </div>
            </CardContent>
          </Card>

          <Card className="stagger-3" data-testid="stat-relationships">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">
                {t('dashboard.activeRelationships')}
              </CardTitle>
              <Users className="h-4 w-4 text-primary" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-primary">
                {stats?.active_relationships || 0}
              </div>
            </CardContent>
          </Card>

          {isSeller ? (
            <Card className="stagger-4" data-testid="stat-products">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t('dashboard.totalProducts')}
                </CardTitle>
                <Package className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.total_products || 0}</div>
              </CardContent>
            </Card>
          ) : (
            <Card className="stagger-4" data-testid="stat-pending-relationships">
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">
                  {t('dashboard.pendingRelationships')}
                </CardTitle>
                <TrendingUp className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold">{stats?.pending_relationships || 0}</div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Quick Actions & Recent Orders - solo per utenti con full buyer features o seller */}
      {(hasFullBuyerFeatures || isSeller) && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle className="font-heading">{t('dashboard.quickActions')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {isBuyer ? (
                <>
                  {/* Nuovo Ordine - solo per utenti con full buyer features */}
                  {hasFullBuyerFeatures && (
                    <Button 
                      className="w-full justify-between" 
                      onClick={() => navigate('/orders')}
                      data-testid="quick-new-order"
                    >
                      <span className="flex items-center gap-2">
                        <Plus className="h-4 w-4" />
                        {t('dashboard.newOrder')}
                      </span>
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}
                  {/* Fornitori - solo per utenti con full buyer features */}
                  {hasFullBuyerFeatures && (
                    <Button 
                      variant="outline" 
                      className="w-full justify-between"
                      onClick={() => navigate('/relationships')}
                      data-testid="quick-suppliers"
                    >
                      <span>Fornitori</span>
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  )}
                </>
              ) : (
                <>
                  <Button 
                    className="w-full justify-between"
                    onClick={() => navigate('/catalog')}
                    data-testid="quick-catalog"
                  >
                    <span className="flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      {t('dashboard.manageProducts')}
                    </span>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="outline" 
                    className="w-full justify-between"
                    onClick={() => navigate('/relationships')}
                    data-testid="quick-clients"
                  >
                    <span>Clienti</span>
                    <ArrowRight className="h-4 w-4" />
                  </Button>
                </>
              )}
              <Button 
                variant="outline" 
                className="w-full justify-between"
                onClick={() => navigate('/chat')}
                data-testid="quick-chat"
              >
                <span>{t('nav.chat')}</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>

          {/* Recent Orders */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="font-heading">{t('dashboard.recentOrders')}</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/orders')}>
                {t('common.all')}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent>
              {stats?.recent_orders?.length > 0 ? (
                <div className="space-y-4">
                  {stats.recent_orders.map((order) => (
                    <div 
                      key={order.id}
                      className="flex items-center justify-between p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => navigate(`/orders/${order.id}`)}
                      data-testid={`recent-order-${order.id}`}
                    >
                      <div className="space-y-1">
                        <p className="font-medium">
                          {t('orders.orderNumber')}{order.id.slice(0, 8)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {new Date(order.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right space-y-1">
                        {getStatusBadge(order.status)}
                        <p className="text-sm font-medium">
                          {formatCurrency(order.total)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  {t('orders.noOrders')}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Dashboard semplificata per utenti Solo Associato */}
      {isAssociatedOnly && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">{t('dashboard.quickActions')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button 
                className="w-full justify-between"
                onClick={() => navigate('/groupage')}
                data-testid="quick-groupage"
              >
                <span className="flex items-center gap-2">
                  <Package className="h-4 w-4" />
                  Groupage
                </span>
                <ArrowRight className="h-4 w-4" />
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-between"
                onClick={() => navigate('/chat')}
                data-testid="quick-chat"
              >
                <span>{t('nav.chat')}</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="font-heading">Il tuo Account</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-sm mb-4">
                Sei registrato come membro dell'associazione. Hai accesso alle funzionalità Groupage e comunicazione.
              </p>
              <Button 
                variant="outline" 
                className="w-full justify-between"
                onClick={() => navigate('/settings')}
              >
                <span>Impostazioni Profilo</span>
                <ArrowRight className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Top Products Section - solo per utenti con full buyer features */}
      {hasFullBuyerFeatures && ((isSeller && stats?.top_products?.length > 0) || (isBuyer && stats?.top_ordered_products?.length > 0)) && (
        <Card data-testid="top-products-section">
          <CardHeader className="flex flex-row items-center justify-between">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" />
              <CardTitle className="font-heading">
                {isSeller ? t('dashboard.topSellingProducts') : t('dashboard.topOrderedProducts')}
              </CardTitle>
            </div>
            <Badge variant="outline" className="text-xs">
              {t('dashboard.last30Days')}
            </Badge>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              {isSeller && stats?.top_products?.map((product, index) => (
                <div 
                  key={product.name}
                  className="flex flex-col p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                  data-testid={`top-product-${index}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                      index === 0 ? 'bg-amber-500 text-white' : 
                      index === 1 ? 'bg-gray-400 text-white' : 
                      index === 2 ? 'bg-amber-700 text-white' : 
                      'bg-muted text-muted-foreground'
                    }`}>
                      {index + 1}
                    </span>
                    <Star className={`h-4 w-4 ${index < 3 ? 'text-amber-500' : 'text-muted-foreground'}`} />
                  </div>
                  <p className="font-medium text-sm line-clamp-2 mb-2" title={product.name}>
                    {product.name}
                  </p>
                  <div className="mt-auto space-y-1">
                    <p className="text-xs text-muted-foreground">
                      {product.quantity} {t('dashboard.unitsSold')}
                    </p>
                    <p className="text-lg font-bold text-primary">
                      {formatCurrency(product.revenue)}
                    </p>
                  </div>
                </div>
              ))}
              {isBuyer && stats?.top_ordered_products?.map((product, index) => (
                <div 
                  key={product.name}
                  className="flex flex-col p-4 rounded-lg border border-border hover:bg-muted/50 transition-colors"
                  data-testid={`top-ordered-product-${index}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                      index === 0 ? 'bg-primary text-white' : 
                      index === 1 ? 'bg-primary/70 text-white' : 
                      index === 2 ? 'bg-primary/50 text-white' : 
                      'bg-muted text-muted-foreground'
                    }`}>
                      {index + 1}
                    </span>
                    <Package className={`h-4 w-4 ${index < 3 ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                  <p className="font-medium text-sm line-clamp-2 mb-2" title={product.name}>
                    {product.name}
                  </p>
                  <div className="mt-auto space-y-1">
                    <p className="text-xs text-muted-foreground">
                      {product.quantity} {t('dashboard.unitsOrdered')}
                    </p>
                    <p className="text-lg font-bold text-primary">
                      {formatCurrency(product.total_spent)}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DashboardPage;
